import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';

const packages = [
  {
    name: 'Demo Package',
    icon: 'ri-record-circle-line',
    price: '',
    description: '',
    extraNote: '',
    features: [
      '2-hour studio session',
      'Up to 2 original tracks',
      'Basic mixing included',
      'Digital file delivery',
      'Engineer provided',
    ],
    highlight: false,
    buttonLabel: 'Schedule a Consultation',
  },
  {
    name: 'Professional Recording Experience',
    icon: 'ri-music-fill',
    price: '',
    description: 'Designed for artists who are ready to elevate their sound with a professional, hands-on studio experience. Each session is customized based on your goals, sound, and creative direction.',
    extraNote: 'Every project is unique. We\'ll work with you to create a recording experience that aligns with your vision and goals.',
    features: [
      'Personalized recording sessions',
      'Professional mixing & mastering available',
      'Creative direction & production support',
      'High-quality digital delivery',
      'Access to musicians & vocal coaching (as needed)',
    ],
    highlight: true,
    buttonLabel: 'Schedule a Consultation',
  },
  {
    name: 'Artist Package',
    icon: 'ri-album-line',
    price: '',
    description: '',
    extraNote: '',
    features: [
      'Full EP or Album project',
      'Multiple recording sessions',
      'Full mixing, mastering & production',
      'Vocal coaching per session',
      'Branding consultation',
      'Physical + digital delivery',
      'Priority scheduling',
    ],
    highlight: false,
    buttonLabel: 'Schedule a Consultation',
  },
];

const services = [
  { icon: 'ri-mic-2-line', title: 'Vocal Recording', desc: 'Crystal-clear vocal capture with professional mic setup and acoustic treatment.' },
  { icon: 'ri-piano-line', title: 'Instrument Tracking', desc: 'Live instrument recording for piano, guitar, bass, drums, and more.' },
  { icon: 'ri-equalizer-line', title: 'Mixing & Mastering', desc: 'Professional audio mixing and mastering for release-ready sound quality.' },
  { icon: 'ri-voiceprint-line', title: 'Voice Overs', desc: 'Clean, professional voice over recordings for media, podcasts, and commercials.' },
  { icon: 'ri-headphone-line', title: 'Beat Production', desc: 'Custom beat and track production from our in-house producers.' },
  { icon: 'ri-film-line', title: 'Soundtrack & Jingles', desc: 'Original music production for films, events, brands, and digital content.' },
];

const studioJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/recording-studio`,
  name: 'Creative Institute Recording Studio',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/recording-studio`,
  description: 'Professional recording studio services in North Carolina. Vocal recording, mixing, mastering, beat production, and full album packages.',
  telephone: '910-242-4567',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Fayetteville',
    addressRegion: 'NC',
    addressCountry: 'US',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Recording Studio Packages',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Demo Package', description: '2-hour studio session, up to 2 original tracks, basic mixing' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Professional Recording Experience', description: 'Personalized recording sessions with mixing, mastering, and creative direction' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Artist Package', description: 'Full EP or Album project with mixing, mastering, and branding consultation' } },
    ],
  },
};

function PageHero() {
  return (
    <section className="relative min-h-[60vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#2D1B69]" />
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7f202b8db676b4906fdae3b25858f821.jpg"
          alt="Creative Institute Recording Studio"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-transparent to-transparent" />
      </div>
      <div className="absolute top-20 right-10 w-72 h-72 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-36">
        <span
          className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.1s' }}
        >
          Professional Recording Services
        </span>
        <h1
          className="font-heading text-white text-6xl lg:text-7xl font-black mb-4"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.35s' }}
        >
          Recording Studio
        </h1>
        <p
          className="font-body text-white/75 text-xl max-w-2xl leading-relaxed"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.6s' }}
        >
          Industry-quality sound production. Book our studio for your next project — from demos to full albums, voice overs to original soundtracks.
        </p>
      </div>

      <style>{`
        @keyframes wordFadeUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function ServicesSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-24 bg-gradient-to-br from-[#0F172A] to-[#1A2550]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            Studio Services
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-white`}>
            What We <span className="text-brand-yellow">Offer</span>
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc, idx) => (
            <div
              key={svc.title}
              className={`scroll-hidden delay-${(idx % 3) * 100 + 100} ${gridVis ? 'scroll-visible' : ''} flex gap-5 p-6 rounded-2xl bg-white/5 border border-white/10 card-lift group hover:bg-white/8 transition-colors`}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/20 text-brand-yellow flex-shrink-0 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-colors duration-300">
                <i className={`${svc.icon} text-xl`} />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-white mb-2">{svc.title}</h3>
                <p className="font-body text-white/50 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-24 bg-gradient-to-br from-[#EEF2FF] via-white to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-14">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            Studio Packages
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark`}>
            Choose Your <span className="gradient-text">Package</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base mt-4`}>
            Every experience is tailored to your vision. Reach out to schedule a consultation and let&apos;s build something great together.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg, idx) => (
            <div
              key={pkg.name}
              className={`scroll-hidden delay-${idx * 150 + 100} ${gridVis ? 'scroll-visible' : ''} rounded-2xl overflow-hidden ${
                pkg.highlight
                  ? 'bg-gradient-to-b from-[#1E3A8A] to-[#5B21B6] text-white'
                  : 'bg-white border border-gray-100'
              } card-lift`}
            >
              {pkg.highlight && (
                <div className="bg-brand-yellow text-brand-blue-dark text-center py-2 font-body text-xs font-bold uppercase tracking-widest">
                  Most Popular
                </div>
              )}
              <div className="p-8">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${pkg.highlight ? 'bg-white/15 text-brand-yellow' : 'bg-brand-blue-dark/10 text-brand-blue-dark'}`}>
                  <i className={`${pkg.icon} text-xl`} />
                </div>
                <h3 className={`font-heading text-2xl font-bold mb-3 ${pkg.highlight ? 'text-white' : 'text-brand-blue-dark'}`}>{pkg.name}</h3>
                {pkg.description && (
                  <p className={`font-body text-sm leading-relaxed mb-5 ${pkg.highlight ? 'text-white/75' : 'text-gray-500'}`}>{pkg.description}</p>
                )}
                <ul className="flex flex-col gap-3 mb-5">
                  {pkg.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`ri-check-line text-sm ${pkg.highlight ? 'text-brand-yellow' : 'text-brand-purple'}`} />
                      </div>
                      <span className={`font-body text-sm ${pkg.highlight ? 'text-white/85' : 'text-gray-600'}`}>{feat}</span>
                    </li>
                  ))}
                </ul>
                {pkg.extraNote && (
                  <p className={`font-body text-xs leading-relaxed italic mb-6 px-4 py-3 rounded-xl ${pkg.highlight ? 'bg-white/10 text-white/70' : 'bg-gray-50 text-gray-500'}`}>
                    {pkg.extraNote}
                  </p>
                )}
                <Link
                  to="/contact"
                  className={`mt-2 block text-center whitespace-nowrap px-6 py-3 rounded-full font-body font-semibold text-sm transition-all cursor-pointer ${
                    pkg.highlight
                      ? 'bg-brand-yellow text-brand-blue-dark hover:bg-brand-yellow-light'
                      : 'border-2 border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white'
                  }`}
                >
                  {pkg.buttonLabel}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  return (
    <section ref={ref} className="py-20 bg-brand-blue-dark text-white text-center">
      <div className="max-w-screen-md mx-auto px-8">
        <h2 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} font-heading text-5xl font-black mb-4`}>
          Ready to Record?
        </h2>
        <p className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-body text-white/70 text-lg mb-8`}>
          Book your studio session today. Serious artists deserve a serious space.
        </p>
        <div className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} flex flex-wrap justify-center gap-4`}>
          <Link to="/client-portal" className="whitespace-nowrap px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base hover:bg-brand-yellow-light transition-all cursor-pointer">
            Book Studio Time
          </Link>
          <a href="tel:9102424567" className="whitespace-nowrap px-8 py-4 rounded-full border-2 border-white/40 text-white font-body font-semibold text-base hover:border-brand-yellow hover:text-brand-yellow transition-all cursor-pointer">
            Call 910-242-4567
          </a>
        </div>
      </div>
    </section>
  );
}

export default function RecordingStudioPage() {
  return (
    <main>
      <SEOHead
        title="Recording Studio | Creative Institute LLC — Fayetteville NC"
        description="Professional recording studio services at Creative Institute LLC in North Carolina. Vocal recording, mixing, mastering, beat production, and full album packages. Book your session today."
        keywords="recording studio Fayetteville NC, professional recording studio North Carolina, music recording NC, vocal recording studio, mixing mastering NC"
        canonical="/recording-studio"
        jsonLd={studioJsonLd}
      />
      <PageHero />
      <ServicesSection />
      <Packages />
      <CTA />
    </main>
  );
}
